/**
 * Helpers puros para descargar un reporte por tramos.
 *
 * El backend solo acepta rangos que quepan en una respuesta (unos 15 días),
 * así que el front parte el rango en tramos, pide uno por uno y une los CSV.
 * Nada de aquí toca HTTP ni Angular: todo recibe y devuelve valores simples
 * para poderse probar sin TestBed.
 */

/** Rango de fechas del reporte, en formato YYYY-MM-DD */
export interface RangoFechas {
  fechaInicio: string;
  fechaFin: string;
}

/** Marca de orden de bytes con la que el backend inicia el CSV (EF BB BF en UTF-8) */
export const BOM = '﻿';

/** Clave `error` del backend cuando el rango no tiene transacciones (404) */
export const ERROR_SIN_TRANSACCIONES = 'recurso no encontrado';

/** Clave `error` del backend cuando el rango no cabe en una respuesta (400) */
export const ERROR_RANGO_EXTENSO = 'rango de fechas demasiado extenso';

/** Días por tramo por defecto. Holgado frente al tope del backend */
export const DIAS_POR_TRAMO = 7;

const MS_POR_DIA = 24 * 60 * 60 * 1000;

/**
 * Convierte YYYY-MM-DD a milisegundos UTC. Se trabaja en UTC para que los
 * cambios de horario no muevan los días.
 * @param fecha fecha en formato YYYY-MM-DD
 */
export function aMilisegundos(fecha: string): number {
  const [anio, mes, dia] = fecha.split('-').map(Number);
  return Date.UTC(anio, mes - 1, dia);
}

/**
 * Formatea milisegundos UTC como YYYY-MM-DD
 * @param ms milisegundos UTC
 */
export function aFecha(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Días que cubre el rango, contando ambos extremos
 * @param rango rango de fechas
 */
export function diasDelRango(rango: RangoFechas): number {
  return Math.round((aMilisegundos(rango.fechaFin) - aMilisegundos(rango.fechaInicio)) / MS_POR_DIA) + 1;
}

/**
 * Parte el rango en tramos consecutivos de `diasPorTramo` días; el último
 * puede ser más corto. Un rango que cabe en un tramo se devuelve tal cual.
 * Si el rango no es válido se devuelve sin partir, para que lo rechace el
 * backend con su propio mensaje.
 * @param rango rango completo que pidió el usuario
 * @param diasPorTramo tamaño de cada tramo
 */
export function partirRango(rango: RangoFechas, diasPorTramo = DIAS_POR_TRAMO): RangoFechas[] {
  const inicio = aMilisegundos(rango.fechaInicio);
  const fin = aMilisegundos(rango.fechaFin);
  if (isNaN(inicio) || isNaN(fin) || fin < inicio || diasPorTramo < 1) {
    return [rango];
  }
  const tramos: RangoFechas[] = [];
  for (let desde = inicio; desde <= fin; desde += diasPorTramo * MS_POR_DIA) {
    const hasta = Math.min(desde + (diasPorTramo - 1) * MS_POR_DIA, fin);
    tramos.push({ fechaInicio: aFecha(desde), fechaFin: aFecha(hasta) });
  }
  return tramos;
}

/**
 * Parte un tramo en dos mitades consecutivas. Si el tramo tiene un solo día
 * ya no se puede partir y se devuelve tal cual.
 * @param tramo tramo a partir
 */
export function partirEnMitades(tramo: RangoFechas): RangoFechas[] {
  const dias = diasDelRango(tramo);
  if (dias <= 1) {
    return [tramo];
  }
  const inicio = aMilisegundos(tramo.fechaInicio);
  const finPrimera = inicio + (Math.ceil(dias / 2) - 1) * MS_POR_DIA;
  return [
    { fechaInicio: tramo.fechaInicio, fechaFin: aFecha(finPrimera) },
    { fechaInicio: aFecha(finPrimera + MS_POR_DIA), fechaFin: tramo.fechaFin }
  ];
}

/**
 * Quita el BOM y la primera línea (el encabezado) de un CSV. Si solo traía
 * el encabezado devuelve vacío.
 * @param csv contenido de un tramo
 */
export function sinEncabezado(csv: string): string {
  const texto = csv.startsWith(BOM) ? csv.slice(BOM.length) : csv;
  const salto = texto.indexOf('\n');
  return salto === -1 ? '' : texto.slice(salto + 1);
}

/**
 * true si el CSV trae al menos una fila además del encabezado
 * @param csv contenido de un tramo
 */
export function tieneFilas(csv: string): boolean {
  return sinEncabezado(csv).trim().length > 0;
}

/**
 * Une varios CSV en uno solo. El primero con contenido va completo (con su
 * BOM y su encabezado); de los siguientes se quita el BOM y el encabezado.
 * No cambia el separador ni el fin de línea: solo agrega un CRLF entre
 * partes cuando la anterior no termina en salto de línea.
 * @param partes contenido de cada tramo, en orden
 */
export function unirCsv(partes: string[]): string {
  const conContenido = partes.filter(parte => parte.length > 0);
  if (conContenido.length === 0) {
    return '';
  }
  const [primera, ...resto] = conContenido;
  let resultado = primera;
  for (const cuerpo of resto.map(sinEncabezado)) {
    if (cuerpo.length === 0) {
      continue;
    }
    if (!resultado.endsWith('\n')) {
      resultado += '\r\n';
    }
    resultado += cuerpo;
  }
  return resultado;
}

/** Lo que devuelve la descarga de un tramo: el CSV, o la clave y el mensaje de error del backend */
export type ResultadoTramo =
  | { ok: true; csv: string }
  | { ok: false; error: string; message: string };

/** Función que descarga un tramo. La inyecta el servicio; en pruebas se simula */
export type DescargarTramo = (tramo: RangoFechas) => Promise<ResultadoTramo>;

/**
 * Aviso de avance. Se llama antes de pedir cada tramo con cuántos van
 * terminados y cuántos hay en total; el total puede crecer si un tramo se
 * parte en dos.
 */
export type Progreso = (hechos: number, total: number) => void;

/** Resultado de la descarga completa */
export interface ResultadoPorTramos {
  ok: boolean;
  /** CSV unido, solo cuando ok */
  csv: string;
  /** Mensaje para el usuario, solo cuando falla */
  message: string;
  /** true cuando todo salió bien pero ningún tramo trajo filas */
  sinDatos: boolean;
}

/**
 * Descarga el rango completo partiéndolo en tramos, en orden y en secuencia,
 * y une los CSV. Reglas por tramo:
 *  - `recurso no encontrado`: no hay transacciones, se omite y se sigue.
 *  - `rango de fechas demasiado extenso`: si tiene más de un día se parte en
 *    dos mitades y se reintentan; si es de un día se aborta con el mensaje.
 *  - cualquier otro error: se reintenta una vez; si vuelve a fallar se aborta.
 * @param rango rango completo que pidió el usuario
 * @param descargarTramo función que pide un tramo al backend
 * @param onProgreso aviso de avance, opcional
 * @param diasPorTramo tamaño inicial de cada tramo
 */
export async function descargarPorTramos(
  rango: RangoFechas,
  descargarTramo: DescargarTramo,
  onProgreso?: Progreso,
  diasPorTramo = DIAS_POR_TRAMO
): Promise<ResultadoPorTramos> {
  const pendientes = partirRango(rango, diasPorTramo);
  const reintentados = new Set<string>();
  const partes: string[] = [];
  let hechos = 0;

  while (pendientes.length > 0) {
    const tramo = pendientes[0];
    if (onProgreso) {
      onProgreso(hechos, hechos + pendientes.length);
    }
    const resultado = await descargarTramo(tramo);

    if (resultado.ok) {
      pendientes.shift();
      partes.push(resultado.csv);
      hechos++;
      continue;
    }
    if (resultado.error === ERROR_SIN_TRANSACCIONES) {
      pendientes.shift();
      hechos++;
      continue;
    }
    if (resultado.error === ERROR_RANGO_EXTENSO) {
      if (diasDelRango(tramo) > 1) {
        pendientes.splice(0, 1, ...partirEnMitades(tramo));
        continue;
      }
      return { ok: false, csv: '', message: resultado.message, sinDatos: false };
    }
    //Cualquier otro error: el mismo tramo se vuelve a pedir una sola vez
    const clave = `${tramo.fechaInicio}_${tramo.fechaFin}`;
    if (!reintentados.has(clave)) {
      reintentados.add(clave);
      continue;
    }
    return { ok: false, csv: '', message: resultado.message, sinDatos: false };
  }

  if (!partes.some(tieneFilas)) {
    return { ok: false, csv: '', message: '', sinDatos: true };
  }
  return { ok: true, csv: unirCsv(partes), message: '', sinDatos: false };
}
